import { z } from 'zod'
import { uuid } from './common.js'

export const userRole = z.enum(['owner', 'assistant'])
export type UserRole = z.infer<typeof userRole>

/**
 * How much explanation to show beside each field.
 *
 * `full` explains what a field is for and what the product will do with what
 * you type. `brief` keeps only the short hints. `off` shows neither.
 *
 * A stored setting rather than a constant, because the point of the
 * explanations is to find out which ones people actually needed. Retiring them
 * is a sequence, not a decision: move to `brief`, see what gets asked about,
 * move to `off`, then delete the `*.guidance` keys from both catalogues. Every
 * sentence lives in the message catalogue precisely so that last step is a
 * deletion rather than an archaeology exercise through JSX.
 */
export const guidanceLevel = z.enum(['full', 'brief', 'off'])
export type GuidanceLevel = z.infer<typeof guidanceLevel>

export const updateGuidanceRequest = z.object({ guidanceLevel })
export type UpdateGuidanceRequest = z.infer<typeof updateGuidanceRequest>

/**
 * Length is the only password rule.
 *
 * NIST 800-63B dropped character-class requirements because they do not produce
 * unguessable passwords — they produce `Senha1!` and a note beside the monitor.
 * Twelve characters of anything is a better floor than eight of four classes.
 *
 * The MAXIMUM is not a formality. argon2 hashes whatever it is handed at 19 MiB
 * a go, so an unbounded field is a way to make the server do arbitrary work.
 * It matches `loginRequest` deliberately: a password that can be set must be
 * one that can be typed back in.
 */
export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 1024

export const loginRequest = z.object({
  email: z.email().max(320),
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
})
export type LoginRequest = z.infer<typeof loginRequest>

/**
 * Rotating your own password.
 *
 * The current password is required even though the caller already holds a
 * session, because a session cookie is exactly what somebody who has stolen one
 * already has. Without this field a captured session converts into permanent
 * ownership of the account, and the rotation this exists to enable would be
 * available to the wrong person first.
 *
 * There is no `confirmNewPassword`. Typing it twice is a defence against a typo
 * in a field you cannot read, which is a property of the form, not of the API —
 * and a rule the server also held would be a rule the two could disagree about.
 */
export const changePasswordRequest = z
  .object({
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    newPassword: z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH),
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    path: ['newPassword'],
    message: 'must differ from the current password',
  })
export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>

/**
 * The authenticated advisor. `agencyId` is present because every downstream
 * query is scoped by it — but note that scoping is enforced by Postgres
 * row-level security, not by this field being passed around correctly.
 */
export const sessionUser = z.object({
  id: uuid,
  email: z.email(),
  displayName: z.string(),
  role: userRole,
  agencyId: uuid,
  agencyName: z.string(),
  guidanceLevel,
})
export type SessionUser = z.infer<typeof sessionUser>

export const sessionResponse = z.object({ user: sessionUser })
export type SessionResponse = z.infer<typeof sessionResponse>
